"use client";

import { FileText, X } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { PdfViewer } from "@/components/lazy-heavy-components";
import { PdfViewerToolbar } from "@/components/pdf-viewer-toolbar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-media-query";
import { useResizableWidth } from "@/hooks/use-resizable-width";
import {
  ASIDE_RESIZE_OPTIONS,
  type AsidePanelResize,
} from "@/hooks/use-aside-view";

export interface PdfViewerPanelProps {
  url: string;
  fileName: string;
  onClose: () => void;
  onOpenInNewWindow?: () => void;
  /** Supplied by the shell's aside manager for every in-app mount. */
  resize?: AsidePanelResize;
}

/**
 * Responsive chat viewer: full-screen dialog on mobile, sidebar on desktop.
 *
 * The shell supplies `resize` for normal in-app asides. The no-manager branch
 * below is retained only for isolated standalone/popout wrappers; current
 * popout routes use PdfViewerPage instead, so it cannot create a competing
 * controller during ordinary app rendering.
 */
export function PdfViewerPanel({
  resize,
  ...props
}: PdfViewerPanelProps) {
  if (resize) return <PdfViewerPanelContent {...props} resize={resize} />;
  return <StandalonePdfViewerPanel {...props} />;
}

function StandalonePdfViewerPanel({
  resize: _resize,
  ...props
}: PdfViewerPanelProps) {
  // Keep the standalone fallback on the manager's central policy too, so its
  // storage key and clamping remain identical if a popup wrapper needs it.
  const resize = useResizableWidth(ASIDE_RESIZE_OPTIONS.pdf);
  return <PdfViewerPanelContent {...props} resize={resize} />;
}

function PdfViewerPanelContent({
  url,
  fileName,
  onClose,
  onOpenInNewWindow,
  resize,
}: Omit<PdfViewerPanelProps, "resize"> & { resize: AsidePanelResize }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    width,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onDoubleClick,
  } = resize;

  const header = (
    <header className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <FileText className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-semibold" title={fileName}>
          {fileName}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <PdfViewerToolbar
          fileUrl={url}
          fileName={fileName}
          onOpenInNewWindow={onOpenInNewWindow}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t("viewer.closeViewer")}
        >
          <X className="size-4" />
        </Button>
      </div>
    </header>
  );

  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent
          className="flex h-full max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0"
          hideCloseButton
          data-testid="pdf-viewer-dialog"
        >
          <DialogTitle className="sr-only">{fileName}</DialogTitle>
          {header}
          <PdfViewer url={url} fileName={fileName} className="min-h-0 flex-1" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <motion.aside
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-background"
      style={{ width }}
      data-testid="pdf-viewer-panel"
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("viewer.dragToResize")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={onDoubleClick}
        className="absolute -left-[3px] top-0 z-20 h-full w-1.5 touch-none cursor-col-resize select-none transition-colors hover:bg-primary/30 active:bg-primary/40"
      />
      {header}
      <PdfViewer url={url} fileName={fileName} className="min-h-0 flex-1" />
    </motion.aside>
  );
}
